import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import { Button, Col, Input, Row } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import Datatable from '../DataTable/Datatable';
import PageHeader from '../PageHeader/PageHeader';
import ContentCustom from '../ContentCustom/ContentCustom';

const { Search } = Input;

const SimpleResourceLandingLayout = ({
  resourceName,
  tradKey,
  dataToFetch,
  columns,
  customActionColumn,
  children,
  populate,
  extraQuery,
  extraHeader,
  extraButtons,
  withCreateButton,
  forceRefresh,
  editAction,
  showAction,
  deleteAction,
  onDoubleClickAction,
  scroll,
  expandable
}) => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const keyword = params.get('k');
  const pageSize = params.get('pS');
  const currentFilters = params.get('f');
  const currentSorter = params.get('s');
  const [searchValue, setSearchValue] = useState(keyword);

  const searchResource = (value) => {
    if (value) {
      navigate({
        pathname,
        search: `?p=1${pageSize ? `&pS=${pageSize}` : ''}${
          currentSorter ? `&s=${currentSorter}` : ''
        }${currentFilters ? `&f=${currentFilters}` : ''}&k=${value}`
      });
    } else {
      navigate({
        pathname,
        search: `?p=1${pageSize ? `&pS=${pageSize}` : ''}${
          currentSorter ? `&s=${currentSorter}` : ''
        }${currentFilters ? `&f=${currentFilters}` : ''}`
      });
    }
  };

  useEffect(() => {
    setSearchValue(null);
  }, [pathname]);

  useEffect(() => {
    if (keyword) setSearchValue(keyword);
    else setSearchValue(null);
  }, [keyword]);

  return (
    <>
      <PageHeader
        title={t(`${tradKey || resourceName}.title`)}
        extra={extraHeader}
      />
      <ContentCustom>
        <Row
          justify="space-between"
          gutter={[8, 16]}
          style={{ marginBottom: 10 }}
        >
          <Col>
            <Search
              allowClear
              placeholder={t('placeholder.search')}
              defaultValue={searchValue}
              onSearch={(value) => searchResource(value)}
            />
          </Col>
          {withCreateButton && (
            <Col>
              <Row align="middle">
                {extraButtons}
                {withCreateButton.button || (
                  <Link to={withCreateButton.path || `${pathname}/create`}>
                    <Button type="add">
                      {`${t('buttons.create')} `}
                      <PlusOutlined />
                    </Button>
                  </Link>
                )}
              </Row>
            </Col>
          )}
        </Row>
        <Row gutter={[8, 16]}>{children}</Row>
        <Datatable
          style={{ marginTop: 24 }}
          resourceName={dataToFetch || resourceName}
          columns={columns}
          customActionColumn={customActionColumn}
          extraQuery={extraQuery}
          populate={populate}
          forceRefresh={forceRefresh}
          editAction={editAction}
          showAction={showAction}
          deleteAction={deleteAction}
          onDoubleClickAction={onDoubleClickAction}
          scroll={scroll || { x: 1200 }}
          expandable={expandable}
        />
      </ContentCustom>
    </>
  );
};

SimpleResourceLandingLayout.propTypes = {
  resourceName: PropTypes.string.isRequired,
  tradKey: PropTypes.string,
  dataToFetch: PropTypes.string,
  columns: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  customActionColumn: PropTypes.bool,
  extraQuery: PropTypes.string,
  extraHeader: PropTypes.element,
  extraButtons: PropTypes.element,
  populate: PropTypes.string,
  withCreateButton: PropTypes.oneOfType([
    PropTypes.bool,
    PropTypes.shape({
      path: PropTypes.string,
      button: PropTypes.element
    })
  ]),
  forceRefresh: PropTypes.bool,
  children: PropTypes.element,
  editAction: PropTypes.oneOfType([
    PropTypes.bool,
    PropTypes.shape({
      pathname: PropTypes.func
    })
  ]),
  showAction: PropTypes.oneOfType([
    PropTypes.bool,
    PropTypes.shape({
      pathname: PropTypes.func
    })
  ]),
  deleteAction: PropTypes.bool,
  onDoubleClickAction: PropTypes.oneOfType([
    PropTypes.bool,
    PropTypes.shape({
      action: PropTypes.func
    })
  ]),
  scroll: PropTypes.shape({}),
  expandable: PropTypes.shape({})
};

SimpleResourceLandingLayout.defaultProps = {
  tradKey: null,
  extraQuery: null,
  extraHeader: null,
  extraButtons: null,
  populate: null,
  customActionColumn: false,
  withCreateButton: true,
  dataToFetch: null,
  forceRefresh: null,
  children: null,
  editAction: false,
  showAction: true,
  deleteAction: true,
  onDoubleClickAction: true,
  scroll: null,
  expandable: undefined
};

export default SimpleResourceLandingLayout;
